import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

const apiUrl = import.meta.env.VITE_APP_API;

const Cart = () => {
    const navigate = useNavigate();
    const [cartItems, setCartItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadCartItems();
    }, []);

    const loadCartItems = () => {
        try {
            const storedCart = localStorage.getItem('cartItems');
            if (storedCart) {
                const items = JSON.parse(storedCart);
                setCartItems(items);
            }
        } catch (error) {
            console.error('Error loading cart items:', error);
        } finally {
            setLoading(false);
        }
    };

    const updateQuantity = (itemId, newQuantity) => {
        if (newQuantity < 1) return;
        
        const updatedItems = cartItems.map(item => 
            item.id === itemId ? { ...item, quantity: newQuantity } : item
        );
        
        setCartItems(updatedItems);
        localStorage.setItem('cartItems', JSON.stringify(updatedItems));
        
        const totalCount = updatedItems.reduce((sum, item) => sum + item.quantity, 0);
        localStorage.setItem('cartCount', totalCount.toString());
        if (window.updateCartCount) {
            window.updateCartCount(totalCount);
        }
    };

    const removeItem = (itemId) => {
        const updatedItems = cartItems.filter(item => item.id !== itemId);
        setCartItems(updatedItems);
        localStorage.setItem('cartItems', JSON.stringify(updatedItems));
        
        const totalCount = updatedItems.reduce((sum, item) => sum + item.quantity, 0);
        localStorage.setItem('cartCount', totalCount.toString());
        if (window.updateCartCount) {
            window.updateCartCount(totalCount);
        }
        
        toast.success('Item removed from cart');
    };

    const calculateTotal = () => {
        return cartItems.reduce((total, item) => {
            const price = item.discount > 0 ? item.price - item.discount : item.price;
            return total + (price * item.quantity);
        }, 0);
    };

    const handleCheckout = () => {
        if (cartItems.length === 0) {
            toast.error('Your cart is empty');
            return;
        }
        
        navigate('/book-checkout');
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-blue-500 mb-4"></div>
                    <p className="text-gray-600 text-lg font-medium">Loading cart...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-12 px-4 sm:px-6 lg:px-8 mt-20">
            <div className="max-w-7xl mx-auto">
                <div className="flex justify-between items-center mb-8">
                    <h1 className="text-3xl font-bold text-gray-900">Your Cart</h1>
                    <Link
                        to="/book-store"
                        className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-200 text-sm font-medium"
                    >
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        Continue Shopping
                    </Link>
                </div>

                <div className="bg-white rounded-2xl shadow-xl p-6">
                    {cartItems.length === 0 ? (
                        <div className="text-center py-12">
                            <div className="bg-pink-50 border border-pink-200 rounded-lg p-8 max-w-md mx-auto">
                                <p className="text-pink-700 font-medium text-lg">
                                    Your Shopping Bag is Empty
                                </p>
                                <Link
                                    to="/book-store"
                                    className="mt-4 inline-block bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-all duration-200"
                                >
                                    Browse Books
                                </Link>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {cartItems.map((item) => (
                                <div key={item.id} className="border border-gray-200 rounded-lg p-4 flex items-center space-x-4 hover:shadow-md transition-shadow duration-200">
                                    <div className="flex-shrink-0">
                                        {item.image ? (
                                            <img
                                                src={`${item.image}`}
                                                alt={item.name}
                                                className="w-24 h-24 object-cover rounded-lg"
                                            />
                                        ) : (
                                            <div className="w-24 h-24 bg-gray-100 rounded-lg flex items-center justify-center">
                                                <span className="text-gray-400 text-sm">No Image</span>
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex-grow">
                                        <h3 className="text-lg font-semibold text-gray-900 mb-1">{item.name}</h3>
                                        <p className="text-green-600 font-bold text-lg">
                                            Tk. {item.discount > 0 ? (item.price - item.discount).toFixed(0) : item.price.toFixed(0)}
                                        </p>
                                    </div>

                                    <div className="flex items-center space-x-3">
                                        <div className="flex items-center space-x-2 bg-gray-100 rounded-lg">
                                            <button
                                                onClick={() => updateQuantity(item.id, item.quantity - 1)}
                                                className="w-10 h-10 text-gray-600 hover:text-gray-800 rounded-l-lg hover:bg-gray-200 transition-colors"
                                            >
                                                -
                                            </button>
                                            <input
                                                type="number"
                                                value={item.quantity}
                                                onChange={(e) => updateQuantity(item.id, parseInt(e.target.value) || 1)}
                                                className="w-16 h-10 text-center border-0 bg-transparent text-gray-900 focus:ring-0"
                                                min="1"
                                            />
                                            <button
                                                onClick={() => updateQuantity(item.id, item.quantity + 1)}
                                                className="w-10 h-10 text-gray-600 hover:text-gray-800 rounded-r-lg hover:bg-gray-200 transition-colors"
                                            >
                                                +
                                            </button>
                                        </div>

                                        <button
                                            onClick={() => removeItem(item.id)}
                                            className="w-10 h-10 bg-red-600 text-white rounded-full flex items-center justify-center hover:bg-red-700 transition-colors"
                                        >
                                            ×
                                        </button>
                                    </div>
                                </div>
                            ))}

                            <div className="border-t pt-6 mt-6">
                                <div className="flex justify-between items-center mb-6">
                                    <span className="text-xl font-semibold text-gray-900">Total:</span>
                                    <span className="text-2xl font-bold text-green-600">
                                        Tk. {calculateTotal().toFixed(0)}
                                    </span>
                                </div>
                                
                                <div className="text-center">
                                    <button
                                        onClick={handleCheckout}
                                        className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-semibold text-lg transition-all duration-200"
                                    >
                                        Proceed to Checkout
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Cart;